import { AddIcon, CloseIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Center,
  Flex,
  Button,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  Spacer,
} from '@chakra-ui/react';
import DataTable from 'react-data-table-component';
import { userClassColumns } from 'utils/columns/userColumns';
import { useState } from 'react';
import ConfirmationActionModal from 'components/common/modals/ConfirmationActionModal';
import { useDispatch, useSelector } from 'react-redux';
import { ReducerStatus } from 'utils/constants';
import { deleteClassUserAction } from 'store/modules/class/class-action';
import { classActions } from 'store/modules/class/class-slice';
import ProgressDisplay from 'components/course/ProgressDisplay';
import { FaChartBar } from 'react-icons/fa';
import { SiMicrosoftexcel } from 'react-icons/si';
import exportAsCsv from 'utils/exportCsv';
import AddUsersMd from '../modals/AddUsersMd';
import ClassUserProgressMd from './ClassUserProgressMd';

const ActionColumn = (handleRemoveUser, handleViewUserProgress) => {
  return {
    name: 'Action',
    selector: (row) => row.value,
    sortable: true,
    cell: (row) => (
      <Flex gap={2}>
        <Tooltip label="View Progress" fontSize="xs">
          <Button
            colorScheme="blue"
            size="xs"
            onClick={() => handleViewUserProgress(row.id)}
          >
            <FaChartBar />
          </Button>
        </Tooltip>

        <Tooltip label="Remove" fontSize="xs">
          <Button
            colorScheme="gray"
            size="xs"
            onClick={() => handleRemoveUser(true, row.id)}
          >
            <CloseIcon />
          </Button>
        </Tooltip>
      </Flex>
    ),
  };
};

const ProgressionColumn = (currentRecords) => {
  const { questSubQuests, progressionRecords } = currentRecords;

  const getUserProgress = (userId) => {
    const userCompletedActivity = progressionRecords.filter(
      (pr) => pr.dateFinished !== null && pr.ownerId === userId
    ).length;

    return (userCompletedActivity / questSubQuests.length) * 100;
  };

  return {
    name: 'Progress',
    sortable: true,
    selector: (row) => getUserProgress(row.id),
    cell: (row) => (
      <ProgressDisplay
        w="100%"
        my={2}
        isLoading={false}
        percentage={getUserProgress(row.id)}
        count={0}
        countLabel=""
      />
    ),
  };
};

function OverallRecord({ ...rest }) {
  const dispatch = useDispatch();

  const classId = useSelector((state) => state.class.class?.id);
  const deleteClassUser = useSelector((state) => state.class.deleteClassUser);
  const users = useSelector((state) => state.class.records.users);
  const progressionRecords = useSelector(
    (state) => state.class.records.progressionRecords
  );
  const getClassRecords = useSelector((state) => state.class.getClassRecords);
  const records = useSelector((state) => state.class.records);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [deleteModal, setDeleteModal] = useState(false);
  const [addUserModal, setAddUserModal] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);

  const filteredUsers = users.filter(
    (item) =>
      (item.name &&
        item.name.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.email &&
        item.email.toLowerCase().includes(filterText.toLowerCase()))
  );

  const handleSetDeleteModal = (isVisible, userId) => {
    setDeleteModal(isVisible);
    setDeleteUserId(userId);
  };

  const handleSetAddUserModal = (isVisible) => {
    setAddUserModal(isVisible);
  };

  const handleDeleteSubmit = () => {
    dispatch(deleteClassUserAction({ classId, userId: deleteUserId }));
  };

  const handleDeleteResult = () => {
    if (deleteClassUser.status === ReducerStatus.SUCCEEDED) {
      dispatch(
        classActions.setDeleteClassUser({
          status: ReducerStatus.IDLE,
          error: null,
        })
      );
      handleSetDeleteModal(false, null);
    } else {
      dispatch(classActions.setDeleteClassUser({ status: ReducerStatus.IDLE }));
    }
  };

  const handleViewUserProgress = (userId) => {
    setSelectedUserId(userId);
    onOpen();
  };

  const handleExport = () => {
    exportAsCsv(progressionRecords, 'class-report');
  };

  return (
    <Box {...rest}>
      <ClassUserProgressMd
        userId={selectedUserId}
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
      />

      <ConfirmationActionModal
        onClose={() => handleSetDeleteModal(false, null)}
        onConfirm={handleDeleteSubmit}
        onSubmitResult={handleDeleteResult}
        isVisible={deleteModal}
        status={deleteClassUser.status}
        description="Are you sure you want to remove this user from the class?"
        successMessage="User removed from class successfully"
        error={deleteClassUser.error}
      />

      <AddUsersMd
        isVisible={addUserModal}
        onClose={() => handleSetAddUserModal(false)}
        classId={classId}
      />

      <Flex m={2} gap={2} align="center">
        <Button
          onClick={handleExport}
          rightIcon={<SiMicrosoftexcel />}
          colorScheme="blue"
          variant="outline"
        >
          Export
        </Button>
        <Spacer />

        <Button
          leftIcon={<AddIcon />}
          aria-label="Add user"
          onClick={() => handleSetAddUserModal(true)}
          colorScheme="red"
          variant="solid"
        >
          Add Users
        </Button>

        <InputGroup width={{ base: '100%', md: '350px' }}>
          <InputRightElement pointerEvents="none">
            <SearchIcon color="gray.300" />
          </InputRightElement>
          <Input
            name="query"
            placeholder="Search"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </InputGroup>
      </Flex>

      <DataTable
        theme={useColorModeValue('default', 'dark')}
        progressPending={getClassRecords.status === ReducerStatus.LOADING}
        columns={[
          ...userClassColumns,
          ProgressionColumn(records),
          ActionColumn(handleSetDeleteModal, handleViewUserProgress),
        ]}
        data={filteredUsers}
        progressComponent={
          <Center my={10}>
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="blue.500"
              size="xl"
            />
          </Center>
        }
        pagination
      />
    </Box>
  );
}

export default OverallRecord;
